"use client"

import { useEffect, useState } from "react"
import axios from "../api/axios"
import { User, Mail, Calendar, Shield, Activity, FileText, Clock, Settings, Bell, Download, Users } from "lucide-react"

function UserPage() {
  const [user, setUser] = useState(null)
  const [userFiles, setUserFiles] = useState([])
  const [userStats, setUserStats] = useState({
    filesUploaded: 0,
    lastLogin: new Date().toISOString(),
    accountCreated: "2023-06-15",
    totalDownloads: 0,
  })
  const [recentActivity, setRecentActivity] = useState([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get("/api/user", { withCredentials: true })
        setUser(userRes.data)

        // Try to fetch user's files
        try {
          const teamsRes = await axios.get("/api/teams_with_files")
          const allFiles = teamsRes.data.flatMap((team) =>
            (team.files || []).map((file) => ({
              ...file,
              teamName: team.name,
            })),
          )

          // Filter files uploaded by current user
          const currentUserFiles = allFiles.filter((file) => file.user?.id === userRes.data.id)
          setUserFiles(currentUserFiles)

          // Update stats with real data
          setUserStats((prev) => ({
            ...prev,
            filesUploaded: currentUserFiles.length,
            accountCreated: userRes.data.created_at
              ? new Date(userRes.data.created_at).toLocaleDateString()
              : prev.accountCreated,
          }))

          // Create recent activity from user's files
          const recentFiles = currentUserFiles
            .filter((file) => file.uploaded_at)
            .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
            .slice(0, 4)
            .map((file) => ({
              action: `Uploaded ${file.filename}`,
              time: new Date(file.uploaded_at).toLocaleDateString(),
              type: "upload",
            }))

          setRecentActivity([...recentFiles, { action: "Logged in to dashboard", time: "Today", type: "login" }])
        } catch (err) {
          console.error("Error fetching files:", err)
        }
      } catch (err) {
        console.error("Error fetching user data:", err)
      }
    }

    fetchData()
  }, [])

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "sky" }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center space-x-4">
        <div className={`p-3 rounded-xl bg-${color}-100`}>
          <Icon size={24} className={`text-${color}-600`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  )

  const QuickAction = ({ icon: Icon, title, description, onClick, color = "sky" }) => (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all text-left group"
    >
      <div className="flex items-center space-x-4">
        <div className={`p-3 rounded-xl bg-${color}-100 group-hover:bg-${color}-200 transition-colors`}>
          <Icon size={24} className={`text-${color}-600`} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 group-hover:text-sky-700 transition-colors">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
    </button>
  )

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
              <div className="w-24 h-24 bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl flex items-center justify-center">
                <span className="text-white font-bold text-3xl">{user.name?.charAt(0).toUpperCase()}</span>
              </div>
              <div className="text-center md:text-left flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user.name}!</h1>
                <p className="text-gray-600 mb-4">
                  Here's your personal dashboard with all your account information and quick actions.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Mail size={16} />
                    <span>{user.email}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar size={16} />
                    <span>Member since {userStats.accountCreated}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Activity size={16} />
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                  {user.team && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Users size={16} />
                      <span>{user.team.name}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* <button className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center space-x-2">
                <Settings size={20} />
                <span>Edit Profile</span>
              </button> */}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FileText}
            title="Files Uploaded"
            value={userStats.filesUploaded}
            subtitle="Total files"
            color="sky"
          />
          <StatCard
            icon={Download}
            title="Downloads"
            value={userStats.totalDownloads}
            subtitle="Total downloads"
            color="green"
          />
          <StatCard
            icon={Clock}
            title="Last Login"
            value="Today"
            subtitle={new Date(userStats.lastLogin).toLocaleString()}
            color="purple"
          />
          <StatCard
            icon={Shield}
            title="Account Status"
            value="Verified"
            subtitle="All systems active"
            color="orange"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QuickAction
                icon={FileText}
                title="Upload Files"
                description="Upload and manage your documents"
                onClick={() => (window.location.href = "/uploadPage")}
                color="sky"
              />
              <QuickAction
                icon={Download}
                title="View Files"
                description="Browse and download your files"
                onClick={() => (window.location.href = "/files")}
                color="green"
              />
              
              
            </div>
          </div>

          {/* Account Information */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Information</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="space-y-6">
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <User size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Full Name</p>
                    <p className="text-sm text-gray-600">{user.name}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Mail size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email Address</p>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Shield size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Account Type</p>
                    <p className="text-sm text-gray-600">
                      {user.role_id === 1 ? "Super Admin" : user.role_id === 2 ? "Admin" : "User"}
                    </p>
                  </div>
                </div>

                {user.team && (
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                    <Users size={20} className="text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Team</p>
                      <p className="text-sm text-gray-600">{user.team.name}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Calendar size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Member Since</p>
                    <p className="text-sm text-gray-600">{userStats.accountCreated}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Activity size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Last Activity</p>
                    <p className="text-sm text-gray-600">{new Date(userStats.lastLogin).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                {/* <button className="w-full bg-sky-50 hover:bg-sky-100 text-sky-700 py-3 px-4 rounded-lg font-medium transition-colors">
                  Update Account Information
                </button> */}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Activity</h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-4 p-4 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div
                      className={`p-2 rounded-full ${
                        activity.type === "upload"
                          ? "bg-sky-100"
                          : activity.type === "download"
                            ? "bg-green-100"
                            : activity.type === "profile"
                              ? "bg-purple-100"
                              : "bg-orange-100"
                      }`}
                    >
                      {activity.type === "upload" && <FileText size={16} className="text-sky-600" />}
                      {activity.type === "download" && <Download size={16} className="text-green-600" />}
                      {activity.type === "profile" && <User size={16} className="text-purple-600" />}
                      {activity.type === "login" && <Activity size={16} className="text-orange-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Activity size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserPage
